import { useState, useRef, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Check,
  CircleUser,
  Search,
  X,
  Clock,
  User,
  ChevronDown,
  ChevronUp,
  CalendarDays,
} from 'lucide-react';
import type { SubTask } from '../../types';
import Avatar from '../common/Avatar';
import DatePickerDropdown from '../common/DatePickerDropdown';
import { useApp } from '../../context/AppContext';

interface SubtaskListProps {
  subtasks: SubTask[];
  onChange: (subtasks: SubTask[]) => void;
}

interface AssigneeComboboxProps {
  assigneeId: string | null;
  onSelect: (userId: string | null) => void;
}

function normalizeDateOnly(dateStr?: string | null): string | null {
  if (!dateStr) return null;

  if (typeof dateStr !== 'string') return null;

  // ISO: 2026-04-07T03:00:00.000Z
  if (dateStr.includes('T')) {
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) return null;

    const year = parsed.getUTCFullYear();
    const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
    const day = String(parsed.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Já está em YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return null;

  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateLabel(dateStr?: string | null) {
  const normalized = normalizeDateOnly(dateStr);
  if (!normalized) return null;

  const [year, month, day] = normalized.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });
}

function AssigneeCombobox({ assigneeId, onSelect }: AssigneeComboboxProps) {
  const { state } = useApp();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const assignee = assigneeId
    ? state.users.find((u) => u.id === assigneeId)
    : null;

  const filtered = state.users.filter((u) =>
    u.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch('');
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
          setSearch('');
        }}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 hover:border-slate-300 bg-white transition-colors text-sm w-full"
        title={assignee ? assignee.name : 'Sem responsavel'}
      >
        {assignee ? (
          <>
            <Avatar name={assignee.name} color={assignee.avatarColor} size="sm" />
            <span className="truncate">{assignee.name}</span>
          </>
        ) : (
          <>
            <CircleUser size={16} className="text-slate-400 shrink-0" />
            <span className="text-slate-400">Sem responsavel</span>
          </>
        )}
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 w-64 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
              <Search size={14} className="text-slate-400 shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar usuario..."
                className="bg-transparent outline-none text-sm text-slate-700 placeholder:text-slate-400 w-full"
                autoFocus
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-52 overflow-y-auto py-1">
            {assignee && (
              <button
                type="button"
                onClick={() => {
                  onSelect(null);
                  setOpen(false);
                  setSearch('');
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <X size={14} />
                <span>Remover responsavel</span>
              </button>
            )}

            {filtered.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => {
                  onSelect(user.id);
                  setOpen(false);
                  setSearch('');
                }}
                className={`flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${user.id === assigneeId
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-700'
                  }`}
              >
                <Avatar name={user.name} color={user.avatarColor} size="sm" />
                <span className="truncate">{user.name}</span>
                {user.id === assigneeId && (
                  <Check size={14} className="ml-auto text-brand-600 shrink-0" />
                )}
              </button>
            ))}

            {filtered.length === 0 && (
              <div className="px-3 py-3 text-sm text-slate-400 text-center">
                Nenhum usuario encontrado
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SubtaskList({ subtasks, onChange }: SubtaskListProps) {
  const completedCount = subtasks.filter((s) => s.completed).length;
  const totalCount = subtasks.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const today = new Date();
  const todayDateOnly = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const editInputRef = useRef<HTMLInputElement>(null);
  const newInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  useEffect(() => {
    if (addingNew && newInputRef.current) {
      newInputRef.current.focus();
    }
  }, [addingNew]);

  function updateSubtask(id: string, partial: Partial<SubTask>) {
    onChange(
      subtasks.map((subtask) => {
        if (subtask.id !== id) return subtask;

        const nextStartDate =
          partial.startDate !== undefined
            ? normalizeDateOnly(partial.startDate)
            : normalizeDateOnly(subtask.startDate);

        let nextEndDate =
          partial.endDate !== undefined
            ? normalizeDateOnly(partial.endDate)
            : normalizeDateOnly(subtask.endDate);

        if (nextStartDate && (!nextEndDate || nextEndDate < nextStartDate)) {
          nextEndDate = nextStartDate;
        }

        return {
          ...subtask,
          ...partial,
          startDate: nextStartDate,
          endDate: nextEndDate,
        };
      })
    );
  }

  function handleAdd() {
    setAddingNew(true);
    setNewTitle('');
  }

  function handleSaveNew() {
    const title = newTitle.trim();

    if (!title) {
      setAddingNew(false);
      setNewTitle('');
      return;
    }

    const newSubtask: SubTask = {
      id: `temp-${Date.now()}`,
      title,
      assigneeId: null,
      hours: 0,
      startDate: null,
      endDate: null,
      completed: false,
    };

    onChange([...subtasks, newSubtask]);
    setAddingNew(false);
    setNewTitle('');
    setExpandedId(newSubtask.id);
  }

  function handleNewKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      handleSaveNew();
    } else if (e.key === 'Escape') {
      setAddingNew(false);
      setNewTitle('');
    }
  }

  function handleToggleComplete(subtask: SubTask) {
    updateSubtask(subtask.id, { completed: !subtask.completed });
  }

  function handleDelete(id: string) {
    onChange(subtasks.filter((subtask) => subtask.id !== id));
    if (expandedId === id) setExpandedId(null);
  }

  function handleTitleClick(subtask: SubTask) {
    setEditingId(subtask.id);
    setEditingTitle(subtask.title);
  }

  function handleTitleSave(id: string) {
    const title = editingTitle.trim();

    if (title) {
      updateSubtask(id, { title });
    }

    setEditingId(null);
    setEditingTitle('');
  }

  function handleTitleKeyDown(e: React.KeyboardEvent, id: string) {
    if (e.key === 'Enter') {
      handleTitleSave(id);
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditingTitle('');
    }
  }

  function isOverdue(subtask: SubTask): boolean {
    const endDate = normalizeDateOnly(subtask.endDate);
    return !!endDate && endDate < todayDateOnly && !subtask.completed;
  }

  function toggleExpand(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h4 className="text-base font-semibold text-slate-800">Subtarefas</h4>
              {totalCount > 0 && (
                <span className="text-sm text-slate-500">
                  {completedCount} de {totalCount} concluidas
                </span>
              )}
            </div>

            {totalCount > 0 && (
              <div className="mt-3 w-full max-w-sm">
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ease-out ${progressPercent === 100 ? 'bg-emerald-500' : 'bg-brand-500'
                      }`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleAdd}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-brand-600 hover:bg-brand-50 rounded-xl transition-colors border border-brand-100 bg-white shrink-0"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Adicionar subtarefa</span>
            <span className="sm:hidden">Adicionar</span>
          </button>
        </div>
      </div>

      {(subtasks.length > 0 || addingNew) && (
        <div className="space-y-2">
          {subtasks.map((subtask) => {
            const overdue = isOverdue(subtask);
            const isExpanded = expandedId === subtask.id;
            const formattedEndDate = formatDateLabel(subtask.endDate);

            return (
              <div
                key={subtask.id}
                className={`rounded-2xl border transition-all ${overdue
                    ? 'bg-red-50/80 border-red-200'
                    : subtask.completed
                      ? 'bg-emerald-50/40 border-emerald-200'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => handleToggleComplete(subtask)}
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-200 ${subtask.completed
                        ? 'bg-emerald-500 border-emerald-500 text-white scale-110'
                        : 'border-slate-300 hover:border-brand-400 bg-white hover:scale-105'
                      }`}
                    aria-label={subtask.completed ? 'Marcar como pendente' : 'Marcar como concluida'}
                  >
                    {subtask.completed && <Check size={11} strokeWidth={3} />}
                  </button>

                  <div className="flex-1 min-w-0">
                    {editingId === subtask.id ? (
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onBlur={() => handleTitleSave(subtask.id)}
                        onKeyDown={(e) => handleTitleKeyDown(e, subtask.id)}
                        placeholder="Titulo da subtarefa..."
                        className="w-full text-sm font-medium bg-transparent outline-none border-b-2 border-brand-300 py-0.5 text-slate-800 placeholder:text-slate-400"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleTitleClick(subtask)}
                        className={`text-left w-full text-sm font-medium transition-colors ${subtask.completed
                            ? 'line-through text-slate-400'
                            : 'text-slate-800 hover:text-brand-600'
                          }`}
                      >
                        <span className="block break-words leading-5">
                          {subtask.title || (
                            <span className="text-slate-400 italic font-normal">Sem titulo</span>
                          )}
                        </span>
                      </button>
                    )}
                  </div>

                  <div className="hidden sm:flex items-center gap-2 shrink-0">
                    {subtask.hours > 0 && (
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md font-medium">
                        {subtask.hours}h
                      </span>
                    )}

                    {formattedEndDate && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-md font-medium ${overdue
                            ? 'bg-red-100 text-red-700'
                            : subtask.completed
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                      >
                        {formattedEndDate}
                      </span>
                    )}

                    {subtask.assigneeId && <AssigneeBadge assigneeId={subtask.assigneeId} />}
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleExpand(subtask.id)}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-slate-700 hover:border-slate-300 transition-colors shrink-0"
                    aria-label={isExpanded ? 'Fechar detalhes' : 'Abrir detalhes'}
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>

                <div className="px-4 pb-3 -mt-1 sm:hidden">
                  <div className="flex flex-wrap items-center gap-2 pl-8">
                    {subtask.hours > 0 && (
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md font-medium">
                        {subtask.hours}h
                      </span>
                    )}

                    {formattedEndDate && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-md font-medium ${overdue
                            ? 'bg-red-100 text-red-700'
                            : subtask.completed
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                      >
                        {formattedEndDate}
                      </span>
                    )}

                    {subtask.assigneeId && <AssigneeBadge assigneeId={subtask.assigneeId} />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-slate-100 animate-fade-in">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      <Field label="Responsavel" icon={User}>
                        <AssigneeCombobox
                          assigneeId={subtask.assigneeId}
                          onSelect={(userId) =>
                            updateSubtask(subtask.id, { assigneeId: userId })
                          }
                        />
                      </Field>

                      <Field label="Horas estimadas" icon={Clock}>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            step={0.5}
                            value={subtask.hours}
                            onChange={(e) =>
                              updateSubtask(subtask.id, {
                                hours: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 bg-white transition-all"
                          />
                          <span className="text-sm text-slate-400 shrink-0">h</span>
                        </div>
                      </Field>

                      <Field label="Inicio" icon={CalendarDays}>
                        <DatePickerDropdown
                          value={normalizeDateOnly(subtask.startDate)}
                          onChange={(val) =>
                            updateSubtask(subtask.id, { startDate: val })
                          }
                          placeholder="Selecionar inicio"
                          showPresets
                        />
                      </Field>

                      <Field label="Termino" icon={CalendarDays}>
                        <DatePickerDropdown
                          value={normalizeDateOnly(subtask.endDate)}
                          onChange={(val) =>
                            updateSubtask(subtask.id, { endDate: val })
                          }
                          placeholder="Selecionar termino"
                          showPresets
                          minDate={normalizeDateOnly(subtask.startDate)}
                        />
                      </Field>
                    </div>

                    <div className="flex justify-end mt-3">
                      <button
                        type="button"
                        onClick={() => handleDelete(subtask.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={13} />
                        Excluir subtarefa
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {addingNew && (
            <div className="rounded-2xl border border-brand-200 bg-brand-50/40 p-4">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full border-2 border-slate-300 bg-white shrink-0" />
                <input
                  ref={newInputRef}
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onBlur={handleSaveNew}
                  onKeyDown={handleNewKeyDown}
                  placeholder="Titulo da nova subtarefa..."
                  className="flex-1 text-sm font-medium bg-transparent outline-none border-b-2 border-brand-300 py-1 text-slate-800 placeholder:text-slate-400"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {subtasks.length === 0 && !addingNew && (
        <div className="text-center py-10 text-sm text-slate-400 rounded-2xl border border-dashed border-slate-200 bg-slate-50">
          Nenhuma subtarefa adicionada
        </div>
      )}
    </div>
  );
}

function AssigneeBadge({ assigneeId }: { assigneeId: string }) {
  const { getUserById } = useApp();
  const user = getUserById(assigneeId);
  if (!user) return null;

  return <Avatar name={user.name} color={user.avatarColor} size="sm" />;
}

function Field({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: React.ElementType | null;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon size={13} className="text-slate-400" />}
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}